import { useCallback, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import zhTWLocale from '@fullcalendar/core/locales/zh-tw'
import enGBLocale from '@fullcalendar/core/locales/en-gb'

import style from './Calendar.module.scss'

import type { Schedule, CalendarEvent } from './schedule'

interface CalendarProps {
  schedule: Schedule[]
  selectedDate?: Date
  onDateSelect?: (date: Date) => void
  onEventClick?: (scheduleId: string) => void
  onEventDrop?: (scheduleId: string, start: Date, end: Date) => void
  onEventResize?: (scheduleId: string, start: Date, end: Date) => void
}

/**
 * Calendar Component
 * @param schedule - schedule data
 * @param selectedDate - selected date
 * @param onDateSelect - on date select callback
 * @param onEventClick - on event click callback
 * @param onEventDrop - on event drop callback
 * @param onEventResize - on event resize callback
 * @returns Calendar Component
 */
function Calendar({
  schedule,
  selectedDate = new Date(),
  onDateSelect,
  onEventClick,
  onEventDrop,
  onEventResize,
}: CalendarProps) {
  const { i18n } = useTranslation()

  const events: CalendarEvent[] = useMemo(
    () =>
      schedule.map((item) => ({
        id: item.id,
        title: item.title,
        start:
          item.startTime instanceof Date
            ? item.startTime
            : new Date(item.startTime),
        end:
          item.endTime instanceof Date ? item.endTime : new Date(item.endTime),
        allDay: item.isAllDay,
        backgroundColor: item.color || '#4CAF50',
        borderColor: item.color || '#4CAF50',
        extendedProps: {
          description: item.description,
          location: item.location,
          status: item.status,
        },
      })),
    [schedule]
  )

  // 日程拖動
  const handleEventDrop = useCallback(
    (dropInfo: any) => {
      const { event } = dropInfo
      onEventDrop?.(event.id, event.start, event.end)
    },
    [onEventDrop]
  )

  // 日程縮放
  const handleEventResize = useCallback(
    (info: any) => {
      const { event } = info
      onEventResize?.(event.id, event.start, event.end)
    },
    [onEventResize]
  )

  return (
    <div className={style.calendar}>
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        events={events}
        initialDate={selectedDate}
        dateClick={(info) => onDateSelect?.(info.date)}
        headerToolbar={{
          left: 'title',
          right: 'today prev,next',
        }}
        locale={i18n.language === 'en-US' ? enGBLocale : zhTWLocale}
        selectable={true}
        editable={true}
        droppable={true}
        eventDrop={handleEventDrop}
        eventResize={handleEventResize}
        eventClick={(info) => onEventClick?.(info.event.id)}
      />
    </div>
  )
}

export default Calendar
